// D29 helpers shared by the provider, model and routing forms: the model kinds a connection may serve (a connection
// restricted to complementary kinds, e.g. OpenRouter with embedding + rerank only, never serves a chat model) and the
// Routing Intelligence economy/premium tier lists (at most 10 ids each). Pure functions, unit-tested without a DOM.
import { MODEL_TARGET_KINDS, type ModelTargetKind } from "@maestros/contracts";
import type { ModelTarget } from "../../api/types-wave2.ts";

export const ALL_MODEL_KINDS: readonly ModelTargetKind[] = MODEL_TARGET_KINDS;

/** Maximum ids per Routing Intelligence tier list (routingIntelligenceConfigSchema). */
export const RI_TIER_MAX = 10;

/** Kinds a Routing Intelligence tier may hold: builder attempts run on chat (or decision) targets only. */
export const RI_TIER_KINDS: readonly ModelTargetKind[] = ["chat", "decision"];

/** Adds or removes one kind, always in the canonical order of MODEL_TARGET_KINDS and without repeats. */
export function toggleKind(kinds: readonly ModelTargetKind[], kind: ModelTargetKind, on: boolean): ModelTargetKind[] {
  const next = new Set(kinds);
  if (on) next.add(kind);
  else next.delete(kind);
  return ALL_MODEL_KINDS.filter((k) => next.has(k));
}

/** Whether a connection serves a kind. A connection without the field (an older API) serves every kind. */
export function kindAllowed(allowed: readonly ModelTargetKind[] | null | undefined, kind: ModelTargetKind): boolean {
  return !allowed || allowed.includes(kind);
}

/** Whether a connection is restricted to a subset of the kinds. */
export function isRestricted(allowed: readonly ModelTargetKind[] | null | undefined): boolean {
  return Boolean(allowed) && ALL_MODEL_KINDS.some((k) => !allowed!.includes(k));
}

/** The kind to keep when the connection changes: the current one when allowed, else the first allowed one. */
export function coerceKind(current: ModelTargetKind, allowed: readonly ModelTargetKind[] | null | undefined): ModelTargetKind {
  if (kindAllowed(allowed, current)) return current;
  return allowed?.[0] ?? current;
}

/** Adds or removes one id of a tier list, keeping its order; adding beyond the maximum leaves the list unchanged. */
export function toggleTierId(ids: readonly string[], id: string, on: boolean, max: number = RI_TIER_MAX): string[] {
  if (!on) return ids.filter((x) => x !== id);
  if (ids.includes(id) || ids.length >= max) return [...ids];
  return [...ids, id];
}

export interface TierOption {
  id: string;
  /** null when the id names no known target (deleted, or not visible): shown so it can still be removed. */
  target: ModelTarget | null;
}

/**
 * Options of a tier list: every chat/decision target that is not disabled, plus any already selected id that falls
 * outside that set (so the person can see and remove it).
 */
export function tierOptions(targets: readonly ModelTarget[], selected: readonly string[]): TierOption[] {
  const candidates = targets.filter((x) => RI_TIER_KINDS.includes(x.kind) && x.state !== "disabled");
  const out: TierOption[] = candidates.map((x) => ({ id: x.id, target: x }));
  for (const id of selected) if (!out.some((o) => o.id === id)) out.push({ id, target: targets.find((x) => x.id === id) ?? null });
  return out;
}
